import { useState } from "react";
import { Check, Facebook, Instagram, Linkedin, Link2, Twitter } from "lucide-react";

import { url } from "@/lib/schema";

const COPY = {
  en: {
    heading: "Share this article",
    facebook: "Share on Facebook",
    twitter: "Share on X (Twitter)",
    linkedin: "Share on LinkedIn",
    instagram: "Copy link for Instagram",
    copy: "Copy link",
    copied: "Link copied",
  },
  tr: {
    heading: "Bu içeriği paylaşın",
    facebook: "Facebook'ta paylaş",
    twitter: "X (Twitter) üzerinde paylaş",
    linkedin: "LinkedIn'de paylaş",
    instagram: "Instagram için bağlantıyı kopyala",
    copy: "Bağlantıyı kopyala",
    copied: "Bağlantı kopyalandı",
  },
} as const;

const ITEM_CLASS =
  "inline-flex size-10 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:border-primary hover:text-primary";

/**
 * Social share row for article pages (news, blog, whitepapers, case studies,
 * trends, FAQ answers). Facebook, X and LinkedIn use their share endpoints;
 * Instagram has no web share URL, so that button copies the canonical link.
 */
export function ShareLinks({
  path,
  title,
  locale = "en",
  className = "",
}: {
  /** Root-relative page path, e.g. "/blog/my-post". */
  path: string;
  title: string;
  locale?: "en" | "tr";
  className?: string;
}) {
  const copy = COPY[locale];
  const [copied, setCopied] = useState(false);
  const shareUrl = url(path);
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);

  const links = [
    {
      key: "facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      label: copy.facebook,
      Icon: Facebook,
    },
    {
      key: "twitter",
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      label: copy.twitter,
      Icon: Twitter,
    },
    {
      key: "linkedin",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      label: copy.linkedin,
      Icon: Linkedin,
    },
  ] as const;

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`.trim()}>
      <span className="text-sm font-semibold text-foreground">{copy.heading}</span>
      <ul className="flex list-none flex-wrap items-center gap-2 p-0">
        {links.map(({ key, href, label, Icon }) => (
          <li key={key}>
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              title={label}
              className={ITEM_CLASS}
            >
              <Icon className="size-4" aria-hidden="true" />
            </a>
          </li>
        ))}
        <li>
          <button
            type="button"
            onClick={copyLink}
            aria-label={copy.instagram}
            title={copy.instagram}
            className={ITEM_CLASS}
          >
            <Instagram className="size-4" aria-hidden="true" />
          </button>
        </li>
        <li>
          <button
            type="button"
            onClick={copyLink}
            aria-label={copy.copy}
            title={copy.copy}
            className={ITEM_CLASS}
          >
            {copied ? (
              <Check className="size-4" aria-hidden="true" />
            ) : (
              <Link2 className="size-4" aria-hidden="true" />
            )}
          </button>
        </li>
      </ul>
      <span aria-live="polite" className="text-xs text-muted-foreground">
        {copied ? copy.copied : ""}
      </span>
    </div>
  );
}
